import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo, stats } from '@/mocks/home';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于91网',
  description: '91网是您的首选平台，提供全面的资源整合与服务入口。了解91网的品牌故事、发展历程、核心优势及联系方式。',
  mainEntity: {
    '@type': 'Organization',
    name: '91网',
    url: 'https://www.91wang.com',
    foundingDate: '2018',
    description: '91网 - 资源整合与服务平台',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-9191',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '中关村科技园区',
      addressLocality: '北京',
      addressRegion: '海淀区',
      addressCountry: 'CN',
    },
  },
};

export default function AboutPage() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    document.title = '关于我们 - 91网品牌介绍与联系方式';
  }, []);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      alert('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }
    try {
      const formData = new FormData(form);
      const submitAddr = form.getAttribute('data-submit-addr');
      if (!submitAddr) return;
      const response = await fetch(submitAddr, {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const responseText = await response.text();
      if (response.ok) {
        let parsed;
        try { parsed = JSON.parse(responseText); } catch { parsed = null; }
        if (parsed?.code === 'OK') {
          alert('感谢您的留言，我们会尽快回复！');
          form.reset();
        } else {
          const errMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
          alert(errMsg);
        }
      } else {
        alert('提交失败，请稍后重试');
      }
    } catch {
      alert('网络错误，请检查网络后重试');
    }
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      <Navbar />
      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="mt-8 mb-16">
            <div className="relative w-full aspect-[3/1] rounded-lg overflow-hidden mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20elegant%20modern%20office%20interior%20with%20soft%20natural%20light%2C%20minimal%20design%2C%20clean%20aesthetic%2C%20cream%20and%20beige%20tones%2C%20professional%20corporate%20atmosphere%2C%20collaborative%20workspace&width=1400&height=466&seq=about-hero-91wang&orientation=landscape"
                alt="91网办公环境"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
              关于91网
            </h1>
            <div className="max-w-3xl">
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                <strong>91网</strong>成立于{siteInfo.foundedYear}年，是国内领先的综合性资源整合与服务平台。我们致力于为用户提供一站式的资源导航和使用体验，聚合了学习资料、实用工具、行业资讯、生活服务等全方位的优质内容。
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                经过多年的发展，91网已经积累了超过{stats[0].value}注册用户，日活跃用户超过{stats[1].value}，入驻服务商超过{stats[2].value}家，累计服务次数超过{stats[3].value}次。我们始终坚持以用户为中心，不断优化产品体验，扩展资源覆盖范围。
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                我们的使命是"让资源触手可及"——通过技术手段打破信息壁垒，让每一位用户都能高效、便捷地获取所需资源，享受数字时代带来的便利。
              </p>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
              品牌信誉
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: 'ri-shield-check-line', title: '安全认证', desc: '通过ISO 27001信息安全认证，数据保护达到国际标准' },
                { icon: 'ri-award-line', title: '行业奖项', desc: '连续三年获得"最佳互联网服务平台"荣誉称号' },
                { icon: 'ri-user-heart-line', title: '用户口碑', desc: 'NPS净推荐值达72分，用户满意度行业领先' },
                { icon: 'ri-building-2-line', title: '合作伙伴', desc: '与3000+优质服务商建立深度合作关系' },
              ].map((item, index) => (
                <div
                  key={index}
                  className="p-6 rounded-lg bg-background-100 border border-background-200/70"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
              发展历程
            </h2>
            <div className="relative max-w-2xl">
              <div className="absolute left-5 top-0 bottom-0 w-px bg-background-300"></div>
              {[
                { year: '2018', title: '初创起步', desc: '91网正式上线，以资源导航为核心功能，首批入驻100家服务商' },
                { year: '2020', title: '快速发展', desc: '注册用户突破100万，上线会员体系和智能推荐功能' },
                { year: '2022', title: '生态扩展', desc: '入驻服务商超过1000家，日均访问量突破30万次' },
                { year: '2024', title: '全面升级', desc: '引入AI智能搜索，上线移动端App，注册用户突破300万' },
                { year: '2026', title: '行业领先', desc: '注册用户突破500万，入驻服务商超过3000家，成为行业标杆平台' },
              ].map((milestone, index) => (
                <div key={index} className="relative pl-14 pb-10 last:pb-0">
                  <div className="absolute left-3.5 top-0 w-3.5 h-3.5 rounded-full bg-accent-500 border-2 border-background-50"></div>
                  <div className="text-xs font-semibold text-accent-600 mb-1">
                    {milestone.year}
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-1">
                    {milestone.title}
                  </h3>
                  <p className="text-sm text-foreground-500">
                    {milestone.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="flex flex-col gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-phone-line text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服电话</h3>
                    <p className="text-sm text-foreground-600">{siteInfo.phone}</p>
                    <p className="text-xs text-foreground-400">周一至周日 9:00-21:00</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-600">{siteInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-map-pin-line text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h3>
                    <p className="text-sm text-foreground-600">{siteInfo.address}</p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-background-100 border border-background-200/70 p-6">
                <h3 className="text-base font-semibold text-foreground-900 mb-5">
                  在线留言
                </h3>
                <ContactForm onSubmit={handleContactSubmit} />
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}

function ContactForm({ onSubmit }: { onSubmit: (e: React.FormEvent<HTMLFormElement>) => void }) {
  return (
    <form
      onSubmit={onSubmit}
      data-readdy-form=""
      data-submit-addr="https://readdy.ai/api/form/d9pmjc1n2trhb35gvi0g"
      className="flex flex-col gap-4"
    >
      <div>
        <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">
          您的姓名 <span className="text-red-500">*</span>
        </label>
        <input
          id="contact-name"
          name="name"
          type="text"
          required
          className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
          placeholder="请输入您的姓名"
        />
      </div>
      <div>
        <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">
          电子邮箱 <span className="text-red-500">*</span>
        </label>
        <input
          id="contact-email"
          name="email"
          type="email"
          required
          className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
          placeholder="请输入您的邮箱"
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">
          留言内容 <span className="text-red-500">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
          placeholder="请输入您的留言（500字以内）"
        ></textarea>
      </div>
      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="honeypot-field"
      />
      <button
        type="submit"
        className="w-full py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
      >
        提交留言
      </button>
    </form>
  );
}